// Шина событий между разделами (без прямых зависимостей между фичами)
import type { Booking, SiteConfig } from '@sayohat/shared';
import type { BookingRequest } from './booking';
import type { HotelsPrefill, LeadContext, Profile, SavedTicket, SearchQuery, TripItem, WatchRequest } from './types';

export interface BusEvents {
  'trip:add': TripItem;
  /** несколько позиций сразу (пакет тура): даты и путешественники дополняют пустой тур */
  'trip:add-many': { items: TripItem[]; title?: string; startDate?: string; endDate?: string; travelers?: { adults: number; children: number } };
  'lead:open': LeadContext;
  'pick:open': SavedTicket[];
  'watch:open': WatchRequest;
  'hotels:prefill': HotelsPrefill;
  'favorites:changed': number;
  /** сохранённые карточки (отели, места, туры) — вторая часть счётчика «Избранного» */
  'saved:changed': number;
  'alerts:changed': number;
  'search:done': SearchQuery;
  /** бронь на сайте отправлена: «Мой тур» отмечает позицию или добавляет новую */
  'booking:done': { booking: Booking; req: BookingRequest };
  'profile:changed': Profile;
  config: SiteConfig;
  [tab: `tab:${string}`]: undefined;
}

export type BusEvent = keyof BusEvents;
type Handler<K extends BusEvent> = (data: BusEvents[K]) => void;
type Args<K extends BusEvent> = BusEvents[K] extends undefined ? [data?: undefined] : [data: BusEvents[K]];

const listeners = new Map<string, Set<(data: unknown) => void>>();

// Разделы и окна загружаются лениво: событие для них может прийти раньше, чем они подписались
// («Найти отель» из билета, «В мой тур», «Следить за ценой», открытие вкладки).
// Такие события без подписчиков придерживаются и доставляются первому подписчику (не старше HOLD_MS).
const HOLD_MS = 30_000;
const isHeld = (name: string) => name.startsWith('tab:')
  || name === 'hotels:prefill' || name === 'lead:open' || name === 'pick:open' || name === 'watch:open'
  || name === 'trip:add' || name === 'trip:add-many';
const held = new Map<string, { at: number; list: unknown[] }>();

export const bus = {
  /** Подписка; возвращает отписку (удобно для useEffect) */
  on<K extends BusEvent>(name: K, fn: Handler<K>): () => void {
    const set = listeners.get(name) ?? new Set();
    listeners.set(name, set);
    const h = fn as (data: unknown) => void;
    set.add(h);
    const wait = held.get(name);
    if (wait) {
      held.delete(name);
      if (Date.now() - wait.at < HOLD_MS) queueMicrotask(() => { if (set.has(h)) wait.list.forEach((d) => h(d)); });
    }
    return () => { set.delete(h); };
  },
  emit<K extends BusEvent>(name: K, ...args: Args<K>): void {
    const set = listeners.get(name);
    if ((!set || !set.size) && isHeld(name)) {
      // «В мой тур» копятся все, остальные — последнее событие
      const prev = held.get(name);
      const list = name.startsWith('trip:') && prev && Date.now() - prev.at < HOLD_MS ? [...prev.list, args[0]] : [args[0]];
      held.set(name, { at: Date.now(), list });
      return;
    }
    for (const fn of [...(set ?? [])]) fn(args[0]);
  },
};
